/**
 * AURA Dictionary Search Bar
 * Search for unclear content in a dictionary/glossary
 */

import { t } from '../core/i18n';
import { getIcon } from './styles/icons';
import { Logger } from '../core/logger';

const logger = new Logger('SearchBar');

export const SEARCH_BAR_STYLES = `
  .aura-search {
    display: flex;
    align-items: center;
    gap: var(--aura-spacing-sm);
    padding: 4px var(--aura-spacing-lg) 6px;
    background: var(--aura-bg-secondary);
    border-bottom: 1px solid var(--aura-border);
  }

  .aura-search__wrapper {
    display: flex;
    align-items: center;
    flex: 1;
    background: var(--aura-bg);
    border: 1px solid var(--aura-border);
    border-radius: var(--aura-radius-full);
    padding: 0 10px;
    transition: border-color var(--aura-transition-fast);
  }

  .aura-search__wrapper:focus-within {
    border-color: var(--aura-border-focus);
    box-shadow: 0 0 0 2px rgba(74, 144, 217, 0.15);
  }

  .aura-search__icon {
    display: flex;
    align-items: center;
    color: var(--aura-text-muted);
    flex-shrink: 0;
  }

  .aura-search__icon svg {
    width: 16px;
    height: 16px;
  }

  .aura-search__input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 5px 6px;
    font-size: 11px;
    color: var(--aura-text);
    outline: none;
    font-family: inherit;
    min-width: 0;
  }

  .aura-search__input::placeholder {
    color: var(--aura-text-muted);
    opacity: 0.8;
  }

  .aura-search__dropdown-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--aura-radius-sm);
    color: var(--aura-text-muted);
    cursor: pointer;
    background: none;
    border: none;
    transition: color var(--aura-transition-fast);
    flex-shrink: 0;
  }

  .aura-search__dropdown-toggle:hover {
    color: var(--aura-text);
  }

  .aura-search__dropdown-toggle svg {
    width: 16px;
    height: 16px;
  }

  .aura-search__results {
    position: absolute;
    top: 100%;
    left: var(--aura-spacing-lg);
    right: var(--aura-spacing-lg);
    background: var(--aura-bg);
    border: 1px solid var(--aura-border);
    border-radius: var(--aura-radius-md);
    box-shadow: var(--aura-shadow);
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
    display: none;
  }

  .aura-search__results--visible {
    display: block;
  }

  .aura-search__result-item {
    padding: var(--aura-spacing-sm) var(--aura-spacing-md);
    font-size: var(--aura-font-size-sm);
    color: var(--aura-text);
    cursor: pointer;
    border-bottom: 1px solid var(--aura-border);
  }

  .aura-search__result-item:last-child {
    border-bottom: none;
  }

  .aura-search__result-item:hover {
    background: var(--aura-bg-hover);
  }

  .aura-search__no-results {
    padding: var(--aura-spacing-md);
    font-size: var(--aura-font-size-sm);
    color: var(--aura-text-muted);
    text-align: center;
  }
`;

interface DictionaryEntry {
  term: string;
  definition: string;
}

const DICTIONARY: DictionaryEntry[] = [
  { term: 'WCAG', definition: 'Web Content Accessibility Guidelines — standards for web accessibility.' },
  { term: 'Screen Reader', definition: 'Software that reads aloud the content displayed on screen for visually impaired users.' },
  { term: 'ARIA', definition: 'Accessible Rich Internet Applications — attributes that define ways to make web content more accessible.' },
  { term: 'Alt Text', definition: 'Alternative text describing images for users who cannot see them.' },
  { term: 'Color Contrast', definition: 'The difference in brightness between foreground and background colors.' },
  { term: 'Focus Indicator', definition: 'A visual indicator showing which element currently has keyboard focus.' },
  { term: 'Seizure', definition: 'A sudden burst of electrical activity in the brain; flashing content can trigger seizures.' },
  { term: 'Dyslexia', definition: 'A learning disorder that involves difficulty reading; readable fonts can help.' },
  { term: 'Motor Disability', definition: 'A condition affecting movement; keyboard navigation provides an alternative to mouse use.' },
  { term: 'Cognitive Load', definition: 'The mental effort required to process information on a page.' },
  { term: 'Reading Guide', definition: 'A horizontal bar that follows the cursor to help users track which line they are reading.' },
  { term: 'High Contrast', definition: 'A display mode that increases the difference between text and background colors.' },
  { term: 'Monochrome', definition: 'A color mode that removes all color, displaying content in shades of gray.' },
];

export function createSearchBar(): { element: HTMLElement; destroy: () => void } {
  const container = document.createElement('div');
  container.className = 'aura-search';
  container.style.position = 'relative';

  const wrapper = document.createElement('div');
  wrapper.className = 'aura-search__wrapper';

  const icon = document.createElement('span');
  icon.className = 'aura-search__icon';
  icon.innerHTML = getIcon('search');

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'aura-search__input';
  input.placeholder = t('searchPlaceholder');
  input.setAttribute('aria-label', t('searchPlaceholder'));
  input.setAttribute('role', 'searchbox');

  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  toggleBtn.className = 'aura-search__dropdown-toggle';
  toggleBtn.innerHTML = getIcon('chevronDown');
  toggleBtn.setAttribute('aria-label', 'Toggle dictionary');

  wrapper.appendChild(icon);
  wrapper.appendChild(input);
  wrapper.appendChild(toggleBtn);

  const results = document.createElement('div');
  results.className = 'aura-search__results';
  results.setAttribute('role', 'listbox');

  container.appendChild(wrapper);
  container.appendChild(results);

  let debounceTimer: ReturnType<typeof setTimeout>;
  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      performSearch(input.value.trim(), results);
    }, 200);
  });

  toggleBtn.addEventListener('click', () => {
    const isVisible = results.classList.contains('aura-search__results--visible');
    if (isVisible) {
      results.classList.remove('aura-search__results--visible');
    } else {
      performSearch('', results);
    }
  });

  input.addEventListener('focus', () => {
    if (input.value.trim().length > 0) {
      performSearch(input.value.trim(), results);
    }
  });

  const handleClickOutside = (e: MouseEvent) => {
    if (!container.contains(e.target as Node)) {
      results.classList.remove('aura-search__results--visible');
    }
  };
  document.addEventListener('click', handleClickOutside);

  const destroy = () => {
    document.removeEventListener('click', handleClickOutside);
    clearTimeout(debounceTimer);
  };

  return { element: container, destroy };
}

function performSearch(query: string, resultsContainer: HTMLElement): void {
  resultsContainer.innerHTML = '';

  const filtered = query.length === 0
    ? DICTIONARY
    : DICTIONARY.filter(
        (entry) =>
          entry.term.toLowerCase().includes(query.toLowerCase()) ||
          entry.definition.toLowerCase().includes(query.toLowerCase())
      );

  if (filtered.length === 0) {
    const noResults = document.createElement('div');
    noResults.className = 'aura-search__no-results';
    noResults.textContent = 'No results found';
    resultsContainer.appendChild(noResults);
  } else {
    filtered.forEach((entry) => {
      const item = document.createElement('div');
      item.className = 'aura-search__result-item';
      item.setAttribute('role', 'option');
      item.innerHTML = `<strong>${entry.term}</strong>: ${entry.definition}`;
      resultsContainer.appendChild(item);
    });
  }

  resultsContainer.classList.add('aura-search__results--visible');
  logger.debug(`Search performed: "${query}", ${filtered.length} results`);
}
